import { useLocation } from 'react-router-dom';
import { useGetScholarsProgramsQuery } from '../../services/graphql/api';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';

// has540 — Resolve which Scholars scholars program should color/label the applicant-details chip.
//
// An applicant has no single scholars program until the cascade admits them (they hold several ranked
// preferences), so the chip's scholars program is CONTEXTUAL to how the viewer arrived, not a property of
// the applicant. Precedence (URL context wins — product decision):
//
//   1. URL query param `?sp=<subProgramId>` — set by Pull Next when navigating here. Lives in the URL
//      so it survives a page refresh / shared link.
//   2. The viewer's own scholars program, when they are a Program Manager (a PM maps to exactly ONE
//      scholars program). Resolved from identity, so it is inherently refresh-proof and needs no URL.
//      Reviewers are intentionally NOT resolved this way: a reviewer may be lent to several programs,
//      so identity is ambiguous — they only get a colored chip when they arrived via Pull Next (rule 1).
//   3. Otherwise null → caller renders the default lavender "Scholars" chip.
//
// Returns { subProgramId, label } for the resolved scholars program, or null.
export default function useChipSubProgram() {
    const location = useLocation();
    const { user = {}, isScholarsProgramManager } = useRoleAuthentication();
    // Only needed to map an id -> label and to match the PM to their program. The query is already
    // cached app-wide (queue, alternate list, reports all use it), so this is effectively free.
    const { data: programs = [] } = useGetScholarsProgramsQuery();

    // Rule 1: explicit URL context wins.
    const urlSubProgramId = new URLSearchParams(location.search).get('sp');
    if (urlSubProgramId) {
        const match = programs.find((p) => p.subProgramId === urlSubProgramId);
        // Label may be missing if programs haven't loaded yet; fall back to the id so the chip still
        // shows the scholars program rather than silently reverting to the default.
        return { subProgramId: urlSubProgramId, label: match?.label ?? urlSubProgramId };
    }

    // Rule 2: Program Manager's own scholars program (exactly one by business rule).
    if (isScholarsProgramManager && user?.osuid) {
        const owned = programs.filter((p) => p.programManagerEmployeeId === user.osuid);
        if (owned.length === 1) {
            return { subProgramId: owned[0].subProgramId, label: owned[0].label ?? owned[0].subProgramId };
        }
    }

    // Rule 3: no context.
    return null;
}
